import ChatBubble from '@mui/icons-material/ChatBubble'
import { parseISO } from 'date-fns'
import MessageIcon from '@mui/icons-material/Message'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import {
  Autocomplete,
  Box,
  Dialog,
  Fade,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  CaseCategory,
  CaseEditor,
  getMessageCaseRelation,
  MessageIdentifier,
  useAddNewCaseRelation,
  useCase,
  useCaseMessageTranscript,
  usePersonCaseCategoriesById,
  useUpdateCase,
  workunitCompleteStates,
  type CaseEditorProps,
} from '~/entities/case'
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion'
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary'
import { useCallback, useEffect, useState } from 'react'
import {
  CarePlansModal,
  listCarePlansQueryKey,
  useCarePlans,
} from '~/features/member-profile/clinical/care-plans'
import {
  CarePlanListResponse,
  CarePlanV2ListResponse,
  ProvidersCasesPartialUpdateResponse,
} from '@fireflyhealth/api'
import { useDispatch, useSelector } from 'react-redux'

import type { FC } from 'react'
import ListIcon from '@mui/icons-material/List'
import { InlineLoader, Loader } from '~/shared/ui'
import { useGroupOptions, UserGroupSelect } from '~/entities/assignee-group'
import { CaseTag, NextActionDate, SlaCountdownPill } from '~/entities/work-unit'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import {
  RawTodoCarePlanResult,
  RawTodoCaseResult,
  RawTodoChatMessageResult,
  RawTodoResult,
} from './PatientToDoService'
import { PatientUser } from '~/shared/types'
import { getUpdatedAtLabel } from '~/shared/lib/dateLabels'
import { listPersonTodoKey } from '~/shared/lib/queryKeys'

import { styled } from '@mui/material/styles'
import { useDrop } from 'react-dnd'
import { useQueryClient } from '@tanstack/react-query'
import InlineEditableField from './InlineEditableField'
import { queueNotification } from '~/shared/notifications/actions'
import CaseMemberFacingInfo from './CaseMemberFacingInfo'
import { EmailListView, useManageSelectedTodo } from '~/features/member-profile/communication'
import { setDueDateToEOD } from '~/shared/lib/date'
import { HEALTH_PLAN_OPERATIONS } from './constants'
import { TodoLinkedEmail, type RawEmailRelation } from './TodoLinkedEmail'
import { CaseHistory } from './CaseHistory'
import { fireAndForget } from '~/shared/lib/fireAndForget'
import { haveChanged } from '~/shared/lib/haveChanged'

const iconSize = '2.2rem'

const ICON_SX = { width: iconSize, height: iconSize }

const HIGHLIGHT_SX = { bgcolor: 'background.highlight' }

const CONTENT_TITLE_SX = { fontWeight: 'bold', color: 'text.primary', fontSize: '1.6rem' }

const INLINE_WRAP_SX = { display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap' }

const ICON_BUTTON_SX = {
  py: 0,
  '&:hover': { backgroundColor: 'transparent' },
  borderRadius: 'unset',
}

const MAIN_ICON_SX = {
  color: 'primary.main',
  justifyContent: 'right',
  display: 'flex',
  pr: '10px',
}

const MAIN_ICON_GREY_SX = { ...MAIN_ICON_SX, color: 'secondary.main' }

const CASE_DESCRIPTION_HOLDER_SX = {
  '&:before': {
    content: '""',
    position: 'absolute',
    backgroundColor: '#E0E0E0',
    width: 'auto',
    height: '1px',
    left: '23px',
    right: '22px',
  },
  display: 'flex',
  alignItems: 'flex-start',
  width: '100%',
}

const PARENT_CATEGORY_SX = {
  ml: iconSize,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  fontSize: '1.2rem',
  color: 'text.secondary',
  display: 'inline-flex',
  mr: '11px',
}

const TEXT_CONTENT_SX = { fontSize: '1.2rem', pl: '4px', fontWeight: 500 }

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion
    disableGutters
    elevation={0}
    square
    slotProps={{ transition: { unmountOnExit: true } }}
    {...props}
  />
))(({ theme }) => ({
  flexGrow: 1,
}))

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
}))

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary {...props} />
))(({ theme }) => ({
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  padding: 0,
}))

const AccordionHeader: React.FC<
  React.PropsWithChildren<{
    highlight?: boolean
    selectedCaseDetail?: number
    onClick?: (e: React.MouseEvent) => void
  }>
> = props => {
  return (
    <AccordionSummary
      aria-label="Expand"
      aria-controls={`${props.selectedCaseDetail}-content`}
      id={`${props.selectedCaseDetail}-header`}
      style={{ flexGrow: 1 }}
      sx={[!!props.highlight && HIGHLIGHT_SX]}
    >
      <Box
        style={{ flexGrow: 1 }}
        onClick={e => {
          const target = (e.target as HTMLElement).closest('.classToStopPropagation')
          const popperTarget = (e.target as HTMLElement).closest('.MuiAutocomplete-popper')
          if (target !== null || popperTarget !== null) {
            e.stopPropagation()
            e.preventDefault()
          } else {
            if (props.onClick) props.onClick(e)
          }
        }}
      >
        {props.children}
      </Box>
    </AccordionSummary>
  )
}

const HeaderNextActionDate = ({
  todo,
  editable = false,
  onChange,
}: {
  todo: RawTodoResult
  editable?: boolean
  onChange?: (dueDate: string) => void
}) => {
  const [dueDate, setDueDate] = useState(todo.due_date)
  const [savedDueDate, setSavedDueDate] = useState(todo.due_date)
  if (todo.due_date !== savedDueDate) {
    setSavedDueDate(todo.due_date)
    setDueDate(todo.due_date)
  }
  return (
    <NextActionDate
      date={dueDate}
      editable={editable}
      newTodos={true}
      onChange={value => {
        setDueDate(value)
        onChange?.(value)
      }}
    />
  )
}

const ToDoHeader: FC<
  React.PropsWithChildren<{
    parentId: number
    todoContent: RawTodoResult
    patient: PatientUser
    expandCase: boolean
    isClosed?: boolean
    disableEdit?: boolean
    parentCategory: string
    carePlanTitle?: string
    isTodoTaskUpdating: boolean
    isTodoNoteUpdating: boolean
  }>
> = props => {
  const {
    parentId,
    todoContent,
    patient,
    children,
    expandCase,
    isClosed,
    disableEdit,
    parentCategory,
    carePlanTitle,
    isTodoTaskUpdating,
    isTodoNoteUpdating,
  } = props

  switch (todoContent.content_type) {
    case 'case': {
      return (
        <CaseHeader
          patient={patient}
          parentId={parentId}
          todoContent={todoContent as RawTodoCaseResult}
          expandCase={expandCase}
          isClosed={isClosed ?? false}
          disableEdit={disableEdit}
          parentCategory={parentCategory}
          carePlanTitle={carePlanTitle}
          isTodoTaskUpdating={isTodoTaskUpdating}
          isTodoNoteUpdating={isTodoNoteUpdating}
        >
          {children}
        </CaseHeader>
      )
    }
    case 'careplan': {
      return (
        <CarePlanHeader
          patient={patient}
          parentId={parentId}
          todoContent={todoContent as RawTodoCarePlanResult}
          parentCategory={parentCategory}
        >
          {children}
        </CarePlanHeader>
      )
    }
    case 'chatmessagev2': {
      return (
        <ChatMessageHeader todoContent={todoContent as RawTodoChatMessageResult}>
          {children}
        </ChatMessageHeader>
      )
    }
  }

  return <Box>Unknown Todo Item</Box>
}

export default ToDoHeader

const CaseHeader: FC<
  React.PropsWithChildren<{
    parentId: number
    todoContent: RawTodoCaseResult
    patient: PatientUser
    expandCase?: boolean
    isClosed?: boolean
    disableEdit?: boolean
    parentCategory: string
    carePlanTitle?: string
    isTodoTaskUpdating: boolean
    isTodoNoteUpdating: boolean
  }>
> = props => {
  const {
    parentId,
    todoContent,
    patient,
    children,
    expandCase,
    isClosed,
    disableEdit,
    parentCategory,
    carePlanTitle,
    isTodoTaskUpdating,
    isTodoNoteUpdating,
  } = props

  const caseEarliestSlaAt = todoContent.earliest_completion_sla_at
  const caseEarliestSlaTaskType = todoContent.earliest_completion_sla_task_type

  const {
    mutate: updateCase,
    mutateAsync: handleUpdateCase,
    isPending: isLoadingUpdateCase,
  } = useUpdateCase()
  const { mutateAsync: mutateAddNewCaseRelation, isPending: isLoadingAddNewCaseRelation } =
    useAddNewCaseRelation(patient.person.id)

  const isUpdatingCase = isLoadingUpdateCase || isLoadingAddNewCaseRelation

  const { mutateAsync: handleUpdateCaseStatus, isPending: isUpdatingCaseStatus } = useUpdateCase()

  const [isFetchingMessageCaseRelation, setIsFetchingMessageCaseRelation] = useState(false)
  const [showEmailModel, setShowEmailModel] = useState<boolean>(false)

  const isCaseMutationInProgress = isFetchingMessageCaseRelation

  const dispatch = useDispatch()

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: ['messageIdentifier', 'caseSelection'],
      /* item is set to any below so that it can accept multiple types during drag and drop. Since we lose some
      // inherent typing with drag and drop, we can either use any or unknown here. We are relatively safe to use
      // any since we do some additional type checking against the drag and drop "getItemType" and assert the type
      based on the specific drag and drop "getItemType". */
      drop: (item, monitor) => {
        const selectedCase = todoContent.content_object
        if (!selectedCase) {
          return
        }
        switch (monitor.getItemType()) {
          case 'caseSelection': {
            return { caseId: selectedCase.id }
          }
          case 'messageIdentifier': {
            // get message case relation for dragged message
            const relation = getMessageCaseRelation(item as MessageIdentifier)
            setIsFetchingMessageCaseRelation(true)
            void mutateAddNewCaseRelation({
              caseObj: selectedCase,
              relationData: relation,
            }).finally(() => {
              setIsFetchingMessageCaseRelation(false)
            })
            break
          }
        }
      },
      // While a case is in the process of being updated, dragging something else into the case could result in an
      // update using stale data, potentially overwriting the previous update (and we don't have optimistic
      // concurrency control to prevent this from happening). To guard against stale updates, prevent dropping while a
      // mutation is in progress (and show a loader or some other indicator).
      canDrop: () => !isCaseMutationInProgress,
      collect: monitor => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [todoContent, isCaseMutationInProgress],
  )
  // The connector returns an element, which React 19's ref callbacks may not;
  // memoised so the wrapper keeps the connector's stable identity and the ref
  // is not detached and reattached on every render.
  const dropRef = useCallback((el: HTMLDivElement | null) => void drop(el), [drop])

  // Scroll to selected case.
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null)
  const { selectedCaseId, selectPayload, navigateToCase, navigateToCarePlan, forwardDraftCaseId } =
    useManageSelectedTodo()

  const [focusDesp, setFocusDesp] = useState<HTMLDivElement | null>(null)
  const [editingCase, setEditingCase] = useState<boolean>(false)
  const [editingCaseForModal, setEditingCaseForModal] = useState<boolean>(false)
  const [viewingCaseHistory, setViewingCaseHistory] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<Element | null>(null)
  const [expandHeader, setExpandHeader] = useState<boolean>(false)
  const [forceExpanded, setForceExpanded] = useState<boolean>(false)

  // Does not pass the personId - since we need the list of all available categories here
  // Ex: System only cases should be visible in the todo list once they have been created by lucian
  const { result: allCaseCategoriesById } = usePersonCaseCategoriesById(null)
  // Incase of person profile we will not have the patient id
  const { data: patientCarePlans } = useCarePlans({
    patientId: patient.id ? patient.id : undefined,
  })

  // The selected case, or the forward-draft target, expands each time it is targeted; the user can
  // collapse it in between.
  const isTargetedCase =
    (!!selectedCaseId && selectedCaseId === parentId) || forwardDraftCaseId === parentId
  const expandTrigger = [
    isTargetedCase,
    selectedCaseId,
    forwardDraftCaseId,
    selectPayload,
    expandCase,
  ]
  const [lastExpandTrigger, setLastExpandTrigger] = useState<unknown[]>()
  if (haveChanged(lastExpandTrigger, expandTrigger)) {
    setLastExpandTrigger(expandTrigger)
    if (isTargetedCase) setForceExpanded(true)
    if (isTargetedCase && expandCase) setExpandHeader(expandCase)
  }

  // A targeted case also takes focus on its add field and scrolls into view.
  useEffect(() => {
    if (!isTargetedCase) return
    if (selectPayload?.type === 'add' && focusDesp?.textContent === '') {
      focusDesp?.focus()
    }
    // Scroll into view if supported (jsdom may not implement it)
    if (scrollEl && typeof scrollEl.scrollIntoView === 'function') {
      scrollEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [
    scrollEl,
    selectedCaseId,
    selectPayload,
    parentId,
    expandCase,
    focusDesp,
    forwardDraftCaseId,
    isTargetedCase,
  ])

  const findCarePlan = (carePlanId: number) => {
    return patientCarePlans?.find(carePlan => carePlan.id === carePlanId)
  }

  // Care plan linked to the selected case (from useCarePlans response, snake_case)
  let carePlan: CarePlanListResponse[number] | null = null

  const selectedCase = todoContent.content_object

  const { selectedCaseOwnerGroupOption, flatOptions } = useGroupOptions(
    selectedCase?.owner_group ?? null,
    false,
    null,
    patient.person.care_team,
    isClosed,
  )

  if (!selectedCase) {
    return <></>
  }

  const caseCategory: CaseCategory | undefined = allCaseCategoriesById[selectedCase.category]
  const caseTitle = caseCategory?.title ?? ''
  const linkedContentCount = (selectedCase.relations ?? []).filter(
    content => content.content_type == 'chatmessagev2' || content.content_type == 'phonecall',
  ).length
  const linkedCarePlanId = (selectedCase.relations ?? []).find(
    content => content.content_type == 'careplan',
  )?.object_id
  if (linkedCarePlanId) {
    carePlan = findCarePlan(linkedCarePlanId) ?? null
  }
  const linkedEmailContent = (selectedCase.relations ?? [])
    .filter(
      // Narrowed on the content object's own discriminant, not the relation's untyped
      // `content_type`, so the email fields below are the generated ones.
      (content): content is RawEmailRelation => content.content_object.content_type === 'email',
    )
    .sort((a, b) => {
      // Sort ascending by email_sent_at (the actual send/receive time from Gmail),
      // falling back to created_at so that [length - 1] in TodoLinkedEmail is always
      // the chronologically latest message.
      const dateA = a.content_object.email_sent_at ?? a.content_object.created_at
      const dateB = b.content_object.email_sent_at ?? b.content_object.created_at
      return dateA < dateB ? -1 : dateA > dateB ? 1 : 0
    })

  const shouldShowExternalEmail = (category: CaseCategory | undefined, hasEmails: boolean) => {
    const isHealthPlanOps = category?.parent === HEALTH_PLAN_OPERATIONS
    const isProviderRequester = category?.requester_type === 'Provider'
    // Show email if (health plan ops OR has existing emails)
    // BUT NOT if it's health plan ops with provider requester and no existing emails
    return (isHealthPlanOps || hasEmails) && !(isHealthPlanOps && isProviderRequester && !hasEmails)
  }

  const emailOnClick = () => {
    setShowEmailModel(!showEmailModel)
    setAnchorEl(null)
  }

  const hasEmails = linkedEmailContent.length > 0
  const showExternalEmail = shouldShowExternalEmail(caseCategory, hasEmails)

  const hasIncompleteTasks = todoContent.items?.some(item => !item.content_object.is_complete)

  // If the case has incomplete tasks, do not show done/deferred status in drop down
  const availableActions =
    hasIncompleteTasks && selectedCase?.states_with_categories
      ? selectedCase.actions?.filter(
          action =>
            !workunitCompleteStates.includes(
              selectedCase?.states_with_categories?.find(
                stateWithCategory => stateWithCategory['state']['name'] == action.dest,
              )?.category as (typeof workunitCompleteStates)[number],
            ),
        )
      : selectedCase.actions

  const onCaseActionChange = async (action: string | undefined) => {
    await handleUpdateCaseStatus({
      id: selectedCase.id,
      body: { action },
    })
  }

  const handleOnAfterChangeCase = (caseObj: ProvidersCasesPartialUpdateResponse) => {
    if (caseObj) {
      navigateToCase(caseObj.id)
    } else {
      dispatch(
        queueNotification({
          variant: 'error',
          message: 'Case Creation Failed',
        }),
      )
    }
  }
  const copyValueToClipboard = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value)
      dispatch(queueNotification({ variant: 'success', message: 'Case id copied to clipboard' }))
    } catch {
      dispatch(queueNotification({ variant: 'error', message: 'Could not copy the case id' }))
    }
  }

  const isSelected = !!selectedCaseId && selectedCaseId === parentId

  return (
    <Accordion
      style={{
        borderTop: '10',
        borderBottom: '10',
        marginBottom: '10',
      }}
      expanded={forceExpanded || forwardDraftCaseId === parentId}
      slotProps={{
        transition: {
          unmountOnExit: true,
          onEntered: () => {
            if (scrollEl && typeof scrollEl.scrollIntoView === 'function') {
              scrollEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          },
        },
      }}
    >
      <AccordionHeader
        highlight={isSelected}
        selectedCaseDetail={selectedCase.id}
        onClick={() => setForceExpanded(current => !current)}
      >
        <div
          ref={dropRef}
          style={{
            backgroundColor: isOver ? 'yellow' : 'inherit',
          }}
        >
          {isCaseMutationInProgress ? <Loader /> : null}
          <div ref={setScrollEl}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', flex: 1 }}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                  }}
                >
                  <Box sx={{ paddingLeft: 2, display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ ...CONTENT_TITLE_SX, ml: 1 }}>{caseTitle}</Typography>
                    <Tooltip title="Member-facing chat thread title" placement="top">
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', ml: 1, lineHeight: 1 }}
                      >
                        {selectedCase.external_description}
                      </Typography>
                    </Tooltip>
                  </Box>
                  {carePlan && workunitCompleteStates.includes(selectedCase.status_category) && (
                    <Box
                      component="button"
                      key={carePlan.id}
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        if (carePlan) {
                          navigateToCarePlan(carePlan.id)
                        }
                      }}
                      sx={{
                        color: 'primary.main',
                        background: 'none',
                        border: 'none',
                        ml: '2rem',
                        mb: '0.5rem',
                        textDecoration: 'underline',
                        '&:hover': { cursor: 'pointer' },
                      }}
                      style={{ display: 'block', padding: '0', opacity: '1' }}
                    >
                      <Box sx={{ paddingLeft: 4 }}>Re: {carePlan?.title}</Box>
                    </Box>
                  )}
                  <InlineEditableField
                    defaultValue={selectedCase?.description ? selectedCase.description : undefined}
                    selectedCase={selectedCase.id}
                    valueToBeUpdated={'description'}
                    placeholder={'+ Add description'}
                    isClosed={isClosed ? isClosed : false}
                    focus={setFocusDesp}
                    showError={selectPayload?.type == 'add'}
                    setEditingCase={setEditingCase}
                  />
                </div>
              </Box>
              <Box
                onClick={e => {
                  e.stopPropagation()
                }}
              >
                <IconButton
                  onClick={e => {
                    setAnchorEl(e.currentTarget)
                  }}
                  size="large"
                  disabled={todoContent.content_type == 'case' && disableEdit}
                  sx={ICON_BUTTON_SX}
                >
                  <MoreVertIcon sx={ICON_SX} />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={e => {
                    setAnchorEl(null)
                  }}
                  slotProps={{
                    list: {
                      disablePadding: true,
                    },
                  }}
                >
                  {!isClosed && (
                    <MenuItem
                      onClick={() => {
                        setEditingCaseForModal(!editingCaseForModal)
                        setAnchorEl(null)
                      }}
                    >
                      Change case
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={() => {
                      setViewingCaseHistory(!viewingCaseHistory)
                      setAnchorEl(null)
                    }}
                  >
                    History
                  </MenuItem>
                  {showExternalEmail && (
                    <MenuItem
                      onClick={() => {
                        setShowEmailModel(!showEmailModel)
                        setAnchorEl(null)
                      }}
                    >
                      Show External Email
                    </MenuItem>
                  )}
                  <MenuItem onClick={() => void copyValueToClipboard(selectedCase.id.toString())}>
                    Copy Case ID: {selectedCase.id}
                  </MenuItem>
                  <CaseMessageTranscriptMenuItem id={selectedCase.id} />
                </Menu>
              </Box>
              <Box
                sx={{
                  marginLeft: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                {editingCase ||
                isLoadingUpdateCase ||
                isLoadingAddNewCaseRelation ||
                isUpdatingCaseStatus ||
                isFetchingMessageCaseRelation ||
                isTodoTaskUpdating ||
                isTodoNoteUpdating ? (
                  <Box
                    sx={theme => ({
                      position: 'relative',
                      justifyContent: 'right',
                      display: 'flex',
                      color: theme.palette.primary.light,
                    })}
                  >
                    <InlineLoader />
                  </Box>
                ) : null}

                <Modal open={editingCaseForModal}>
                  <Fade in={editingCaseForModal}>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '40%',
                      }}
                    >
                      <Box
                        onClick={e => {
                          e.stopPropagation()
                        }}
                      >
                        <ExistingCaseEditor
                          patient={patient}
                          handleClose={() => setEditingCaseForModal(!editingCaseForModal)}
                          caseId={selectedCase.id}
                          onAfterSave={handleOnAfterChangeCase}
                        />
                      </Box>
                    </Box>
                  </Fade>
                </Modal>
                <Dialog
                  open={viewingCaseHistory}
                  onClose={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    setViewingCaseHistory(false)
                  }}
                >
                  <Paper
                    style={{ maxHeight: '90vh', overflow: 'auto' }}
                    onClick={e => {
                      e.stopPropagation()
                    }}
                  >
                    <CaseHistory id={selectedCase.id} />
                  </Paper>
                </Dialog>
                {showEmailModel && (
                  <EmailListView
                    selectedCaseId={selectedCase.id}
                    onClose={() => setShowEmailModel(false)}
                  />
                )}
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                mt: 2,
                mb: 2,
                ml: 2,
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={INLINE_WRAP_SX}>
                <Box
                  sx={{
                    '& .MuiInputBase-root.MuiOutlinedInput-root': { fontSize: '1.4rem' },
                    display: 'flex',
                    mr: 1,
                    '& .MuiAutocomplete-root .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
                      maxWidth: '105px',
                    },
                  }}
                >
                  <UserGroupSelect
                    selectedValue={selectedCaseOwnerGroupOption ?? null}
                    options={flatOptions}
                    onChange={ownerGroupOption => {
                      const ownerGroup = ownerGroupOption.value
                      if (ownerGroup) {
                        updateCase({ id: selectedCase.id, body: { owner_group: ownerGroup } })
                      }
                    }}
                    detailed
                    disabled={isClosed || isUpdatingCase}
                  />
                </Box>
                <Autocomplete
                  disableClearable={true}
                  style={{ width: 180 }}
                  autoHighlight
                  value={{
                    dest: selectedCase.status || '',
                  }}
                  options={availableActions || []}
                  getOptionLabel={option =>
                    option.trigger && option.trigger !== option.dest
                      ? `${option.trigger} (${option.dest})`
                      : option.dest
                  }
                  sx={{ '& .MuiAutocomplete-input': { fontSize: '1.4rem' } }}
                  slotProps={{
                    listbox: { sx: { '& .MuiAutocomplete-option': { fontSize: '1.4rem' } } },
                  }}
                  onChange={(event, newValue) => {
                    // If we are on the inactive tab, we are probably
                    // reopening the case so we can continue to make changes
                    // to it. Navigate to the case so we don't lose it if it
                    // moves to another tab.
                    if (workunitCompleteStates.includes(selectedCase.status_category)) {
                      navigateToCase(selectedCase.id)
                    }

                    event.stopPropagation()
                    fireAndForget('[Todos] change case action', () =>
                      onCaseActionChange(newValue?.trigger),
                    )
                  }}
                  renderInput={params => (
                    <TextField
                      onClick={e => {
                        e.stopPropagation()
                      }}
                      {...params}
                      size="small"
                      sx={{
                        '& fieldset': { border: 'none' },
                        '&': { backgroundColor: '#f5f5f5', borderRadius: '10px' },
                      }}
                    />
                  )}
                  disabled={isUpdatingCaseStatus}
                />
                <Box>
                  <HeaderNextActionDate
                    todo={todoContent}
                    editable={!isClosed}
                    onChange={dueDate =>
                      updateCase({
                        id: selectedCase.id,
                        body: { due_date: dueDate ? setDueDateToEOD(dueDate) : undefined },
                      })
                    }
                  />
                </Box>
              </Box>
              <Box sx={INLINE_WRAP_SX}>
                <Box
                  style={{ minWidth: '46px' }}
                  sx={linkedContentCount > 0 ? MAIN_ICON_SX : MAIN_ICON_GREY_SX}
                >
                  <Box sx={{ mr: 1 }}>
                    <SlaCountdownPill
                      earliestCompletionSlaAt={caseEarliestSlaAt}
                      earliestCompletionSlaTaskType={caseEarliestSlaTaskType}
                      messageCount={linkedContentCount}
                      onClick={() => navigateToCase(parentId)}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box sx={{ ...CASE_DESCRIPTION_HOLDER_SX, justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', marginTop: 1 }}>
                <Typography sx={{ ...PARENT_CATEGORY_SX, marginTop: -1, pt: 1 }}>
                  {parentCategory}
                  {carePlanTitle ? ` / ` + carePlanTitle : ''}
                </Typography>
                {selectedCase.case_tags?.map(caseTag => {
                  return (
                    <CaseTag
                      tag={caseTag.tag}
                      key={String(caseTag.id)}
                      tagKey={String(caseTag.id)}
                    />
                  )
                })}
                <Typography sx={{ color: 'text.secondary', marginLeft: 1, fontSize: '1.2rem' }}>
                  {selectedCase.updated_at
                    ? `Updated ${getUpdatedAtLabel(parseISO(selectedCase.updated_at))}`
                    : ''}
                </Typography>
              </Box>
              <Box sx={{ mr: 1, mt: 1 }}>
                <HeaderExpandIcon
                  expandCase={selectedCaseId === parentId ? expandCase : false}
                  todoContent={todoContent}
                  expandHeader={() => setExpandHeader(!expandHeader)}
                />
              </Box>
            </Box>
            <TodoLinkedEmail linkedEmailContent={linkedEmailContent} emailOnClick={emailOnClick} />
          </div>
        </div>
      </AccordionHeader>
      <AccordionDetails>
        {children}
        {caseCategory?.uses_external_description ? (
          <CaseMemberFacingInfo
            selectedCase={selectedCase}
            handleSubmit={async values => {
              await handleUpdateCase({
                id: selectedCase.id,
                body: values,
              })
            }}
            loading={isLoadingUpdateCase}
          />
        ) : null}
      </AccordionDetails>
    </Accordion>
  )
}

// ExistingCaseEditor is a helper for lazily loading an existing case by ID, and then making it editable in the
// standard CaseEditor.
const ExistingCaseEditor = (props: Omit<CaseEditorProps, 'case'> & { caseId: number }) => {
  const { selectedCaseId, navigateToCase } = useManageSelectedTodo()
  const { isPending: isLoading, data: patientCase } = useCase(props.caseId, () => {
    if (selectedCaseId) navigateToCase(null)
  })

  if (isLoading) {
    return <Loader />
  }

  return <CaseEditor {...props} case={patientCase} />
}

const CaseMessageTranscriptMenuItem = (props: { id: number }) => {
  const [isCopying, setIsCopying] = useState(false)
  const { isPending: isLoading, data } = useCaseMessageTranscript(props.id)

  // Copy summary to clipboard when requested and ready.
  useEffect(() => {
    if (!isCopying) {
      return
    }

    if (isLoading || !data) {
      return
    }

    fireAndForget('[Todos] copy note to clipboard', () => navigator.clipboard.writeText(data.text))
  }, [isCopying, isLoading, data])

  return (
    <MenuItem
      onClick={e => {
        e.stopPropagation()
        setIsCopying(true)
      }}
    >
      {isCopying ? isLoading ? <Loader /> : 'Copied' : 'Copy Message Transcript'}
    </MenuItem>
  )
}

const CarePlanHeader: FC<
  React.PropsWithChildren<{
    parentId: number
    todoContent: RawTodoCarePlanResult
    patient: PatientUser
    parentCategory: string
  }>
> = props => {
  const { parentId, todoContent, patient, children } = props
  const client = useQueryClient()
  const me = useSelector(state => state.me)

  const [editingCareplan, setEditingCareplan] = useState<boolean>(false)

  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null)
  const [expandHeader, setExpandHeader] = useState<boolean>(false)
  const { selectedCarePlanId } = useManageSelectedTodo()

  useEffect(() => {
    if (!selectedCarePlanId || selectedCarePlanId !== parentId) {
      return
    }

    scrollEl?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [scrollEl, selectedCarePlanId, parentId])

  const selectedCarePlan = todoContent.content_object
  if (!selectedCarePlan) {
    return <></>
  }
  // Adapt the lightweight care plan from the todo into the snake_case shape expected by CarePlansModal
  const carePlanForModal: CarePlanV2ListResponse[number] = {
    id: selectedCarePlan.id,
    title: selectedCarePlan.title,
    action: selectedCarePlan.action,
    actions: selectedCarePlan.actions,
    status: selectedCarePlan.status,
    status_category: selectedCarePlan.status_category,
    created_at: selectedCarePlan.created_at,
    created_by: selectedCarePlan.created_by,
    updated_at: selectedCarePlan.updated_at,
    updated_by: selectedCarePlan.updated_by,
    patient: selectedCarePlan.patient,
    // Minimal fields expected by the modal when editing
    notes: '',
    tasks: [],
    assigned_tasks: [],
    // Safe defaults for fields not present on the todo contentObject
    type_id: 0,
    type_uid: '',
    cases: [],
    states_with_categories: [],
    is_case_based_care_plan: false,
    care_plan_education_resources: [],
    measure_collection_measures: [],
    visible_to_patient: selectedCarePlan.visible_to_patient,
  }

  const isSelectedCarePlan = !!selectedCarePlanId && selectedCarePlanId === parentId

  const closeModal = () => {
    if (patient.id) {
      void client.invalidateQueries({ queryKey: listCarePlansQueryKey(patient.id) })
    }
    void client.cancelQueries({ queryKey: [listPersonTodoKey, patient.person.id] })
    void client.invalidateQueries({ queryKey: [listPersonTodoKey, patient.person.id] })
    setEditingCareplan(!editingCareplan)
  }

  return (
    <div>
      <Accordion defaultExpanded={!!selectedCarePlanId && selectedCarePlanId === parentId}>
        <AccordionHeader>
          <div ref={setScrollEl}>
            <Box
              sx={[
                isSelectedCarePlan && HIGHLIGHT_SX,
                {
                  paddingLeft: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                },
              ]}
            >
              <Box sx={{ display: 'flex', flexGrow: 1 }}>
                <Typography sx={CONTENT_TITLE_SX}>
                  {selectedCarePlan ? selectedCarePlan.title : ''}
                </Typography>
              </Box>
              {editingCareplan && (
                <Box
                  onClick={e => {
                    e.stopPropagation()
                  }}
                >
                  <CarePlansModal
                    closeModal={closeModal}
                    carePlan={carePlanForModal}
                    open={editingCareplan}
                    patient={patient}
                    me={me}
                    handleModalAction={() => {}}
                  />
                </Box>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                mt: 2,
                mb: 2,
                ml: 2,
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={INLINE_WRAP_SX}>
                <Box sx={{ width: '180px', mr: 1 }}></Box>
                <Box sx={{ width: '180px' }}></Box>
                <Box>
                  <HeaderNextActionDate todo={todoContent} />
                </Box>
              </Box>
              <Box sx={INLINE_WRAP_SX}>
                <Box sx={MAIN_ICON_SX} style={{ minWidth: '46px' }}>
                  <HeaderExpandIcon
                    todoContent={todoContent}
                    expandHeader={() => setExpandHeader(!expandHeader)}
                  />
                  <IconButton size="large" sx={ICON_BUTTON_SX}>
                    <ChatBubble sx={ICON_SX} />
                    <Typography sx={TEXT_CONTENT_SX}>{'msgs not available'}</Typography>
                  </IconButton>
                </Box>
              </Box>
            </Box>
            <Box sx={{ ...CASE_DESCRIPTION_HOLDER_SX, mt: 1 }}>
              <Box sx={{ width: '70%' }}>
                <Typography sx={{ ...PARENT_CATEGORY_SX, pt: 0.5 }}>{'Care Plan'}</Typography>
              </Box>
              <Box sx={{ width: '30%', alignSelf: 'flex-end' }}>
                <Box
                  sx={{
                    color: 'text.secondary',
                    justifyContent: 'right',
                    display: 'flex',
                    pr: '20px',
                  }}
                >
                  <Typography sx={{ fontSize: '1.2rem' }}>
                    {selectedCarePlan.updated_at
                      ? `Updated ${getUpdatedAtLabel(parseISO(selectedCarePlan.updated_at))}`
                      : ''}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </div>
        </AccordionHeader>
        <AccordionDetails>{children}</AccordionDetails>
      </Accordion>
    </div>
  )
}

const ChatMessageHeader: FC<
  React.PropsWithChildren<{
    todoContent: RawTodoChatMessageResult
  }>
> = props => {
  const { todoContent, children } = props
  const [anchorEl, setAnchorEl] = useState<Element | null>(null)
  const [expandHeader, setExpandHeader] = useState<boolean>(false)
  return (
    <Accordion expanded={expandHeader}>
      <AccordionHeader>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ paddingLeft: 3, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
            <MessageIcon sx={ICON_SX} />
            <Box sx={{ paddingLeft: 1 }}>
              <Typography sx={CONTENT_TITLE_SX}>messages</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <HeaderNextActionDate todo={todoContent} />
            <Box style={{ minWidth: '46px' }}></Box>
            <HeaderExpandIcon
              todoContent={todoContent}
              expandHeader={() => setExpandHeader(!expandHeader)}
            />
            <div>
              <IconButton
                onClick={e => {
                  setAnchorEl(e.currentTarget)
                }}
                size="large"
                sx={ICON_BUTTON_SX}
              >
                <MoreVertIcon sx={ICON_SX} />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={e => {
                  setAnchorEl(null)
                }}
                slotProps={{
                  list: {
                    disablePadding: true,
                  },
                }}
              ></Menu>
            </div>
          </Box>
        </Box>
      </AccordionHeader>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  )
}

const HeaderExpandIcon: FC<{
  numberOfChildren?: number
  todoContent?: RawTodoResult
  expandHeader: () => void
  expandCase?: boolean
}> = props => {
  const { numberOfChildren, todoContent, expandHeader, expandCase } = props
  const [isOpen, setIsOpen] = useState<boolean>(false)

  // Opens when the case is expanded from the navigation tab.
  const [openedForExpandCase, setOpenedForExpandCase] = useState<boolean>()
  if (expandCase !== openedForExpandCase) {
    setOpenedForExpandCase(expandCase)
    if (expandCase) setIsOpen(expandCase)
  }

  // If neither todoContent or numberOfChildren are passed, we just add an empty character to the expand icon
  let subtitle = 0
  // If numberOfChildren is passed in, this will take priority
  if (numberOfChildren) {
    subtitle = numberOfChildren
  }
  // Else, we look at the todoContent to determine nuber of children
  else if (todoContent) {
    // We only want to show the number of incomplete tasks, unless all tasks are complete
    const areTasksHidden: boolean =
      todoContent.items.some(item => item.content_object.is_complete) &&
      todoContent.items.some(item => !item.content_object.is_complete)
    const lengthOfTasks = todoContent.items.filter(todoItem => {
      if (!areTasksHidden) {
        return true
      }

      return !todoItem.content_object.is_complete
    }).length
    subtitle = lengthOfTasks
  }

  return (
    <IconButton
      onClick={() => {
        expandHeader()
        setIsOpen(!isOpen)
      }}
      size="large"
      color={subtitle > 0 ? 'primary' : 'secondary'}
      sx={theme => ({
        ...ICON_BUTTON_SX,
        '&:hover': {
          backgroundColor: 'transparent',
          color: subtitle > 0 ? theme.palette.primary[700] : '',
        },
      })}
    >
      <ListIcon sx={ICON_SX} />
      <Typography sx={TEXT_CONTENT_SX} style={{ marginLeft: 1 }}>
        {`${subtitle} ${subtitle > 1 || subtitle == 0 ? 'tasks' : 'task'}`}
      </Typography>
    </IconButton>
  )
}
